import { Fragment, useEffect, useMemo } from "react";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useDashboardChart } from "@/hooks/api";
import { useCompanies } from "@/hooks/api/useCompanies";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Hint } from "@/components/ui/hint";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/cn";
import { downloadCsv, filenameSlug, toCsv } from "@/lib/utils/csv";
import {
  DashboardTable,
  useDashboardTable,
  type DashboardTableColumn,
  type DashboardTableHeaderRow,
} from "./dashboard-table";
import { useDashboardUrlState } from "./useDashboardUrlState";
import { FiltersCard } from "./filters-card";
import { MultiSelect } from "./multi-select";

const DASHBOARD_ID = "b2b-economics";

// Semicolon-separated: Excel on Norwegian locale reads comma-separated CSV as
// a single column.
const CSV_DELIMITER = ";";

/**
 * Decimals come off the API dot-separated ("4499.22"). nb-NO Excel — the whole
 * reason the delimiter above is a semicolon — reads those as text, which makes
 * every money column in the export unusable for arithmetic. Swap in the comma.
 */
function csvDecimal(value: string | number | null | undefined): string {
  if (value == null || value === "") return "";
  return String(value).replace(".", ",");
}

const FLOW_LABELS: Record<string, string> = {
  healthCheck: "Helsekontroll",
  consumer: "Vanlig b2c produkt",
  drDropinBht: "Dr.Dropin BHT",
  kickstart: "Kickstart",
};

/**
 * One meaning per colour, used identically in every chart, table and KPI on
 * the page: money in is green, the three things that eat it are one warm ramp
 * ordered the way they are stacked, what the state covers is blue because it
 * is neither our income nor our cost, and a loss is red.
 *
 * Tokens rather than hex, because a hardcoded colour does not follow the
 * theme picked on /settings — see backoffice/CLAUDE.md.
 */
const COLOR = {
  /** Money we keep: income, and a positive margin. */
  income: "var(--color-admin-success-500)",
  /** The three things that eat it, lightest to darkest in stacking order. */
  sampling: "var(--color-admin-caution-400)",
  markers: "var(--color-admin-warn-500)",
  assessment: "var(--color-admin-warn-700)",
  /** What the state covers — neither our income nor our cost. */
  helfo: "var(--color-admin-sky-400)",
  /** A margin that has gone negative. */
  loss: "var(--color-admin-danger-400)",
} as const;

/**
 * Total cost deliberately has no colour of its own. It is the sum of the
 * three warm buckets, so painting it one of their three would claim it *is*
 * that bucket, and a fourth warm shade would just be a colour nobody can
 * name. The breakdown carries the colour; the total carries the number.
 */

const axisStyle = {
  fontSize: 11,
  fill: "var(--color-admin-text-subtle)",
} as const;
const GRID_STROKE = "var(--color-admin-card-border)";

/** The chart shows the most-used markers; the table below it lists them all. */
const MARKER_CHART_ROWS = 20;

function isoDate(d: Date): string {
  return d.toISOString().split("T")[0]!;
}

const PERIOD_PRESETS: { label: string; from: string; to: string }[] = (() => {
  const now = new Date();
  const today = isoDate(now);
  const year = now.getFullYear();
  // All-UTC, and by month rather than year: mixing a local getFullYear() with
  // setUTCFullYear() collapses the range to a single day either side of New
  // Year, and Feb 29 rolls over to Mar 1.
  const twelveMonthsAgo = new Date(now);
  twelveMonthsAgo.setUTCMonth(twelveMonthsAgo.getUTCMonth() - 12);

  return [
    { label: "I år", from: `${year}-01-01`, to: today },
    { label: "Siste 12 mnd", from: isoDate(twelveMonthsAgo), to: today },
    { label: "I fjor", from: `${year - 1}-01-01`, to: `${year - 1}-12-31` },
    { label: "Fra start", from: "2026-05-01", to: today },
  ];
})();

/** Values arrive as decimal strings in NOK (not øre, unlike `invoices.amount`). */
function nok(value: string | number | null | undefined): string {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("no-NO", { maximumFractionDigits: 0 });
}

function pct(value: number | null | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toLocaleString("no-NO", { maximumFractionDigits: digits })} %`;
}

function count(n: number): string {
  return n.toLocaleString("no-NO");
}

/** "1 bedrift" / "2 bedrifter" — Norwegian plurals are not an -s away. */
function plural(n: number, one: string, many: string): string {
  return `${count(n)} ${n === 1 ? one : many}`;
}

/** Outstanding health checks across a set of companies. */
function gapVisits(rows: CompanyRow[]): number {
  return rows.reduce((a, r) => a + r.outstanding_visits, 0);
}

/**
 * "Bare Kø AS (2) og Helt Ny AS (1)" — the companies behind a gap, not a
 * count of them. A count tells a reader something is missing; the name tells
 * them where to go and fix it, and these sets are small by construction.
 * Long ones stop at six and put the rest in the tooltip, so the paragraph
 * cannot grow into a wall.
 */
const NAMED_COMPANY_LIMIT = 6;

function namedCompanies(rows: CompanyRow[]): React.ReactNode {
  // The count per company only earns its place when there is more than one to
  // tell apart — the sentence already carries the total.
  const label = (r: CompanyRow) =>
    rows.length === 1
      ? r.company_name
      : `${r.company_name} (${count(r.outstanding_visits)})`;
  if (rows.length <= NAMED_COMPANY_LIMIT) {
    const names = rows.map(label);
    return names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(", ")} og ${names[names.length - 1]}`;
  }
  const shown = rows.slice(0, NAMED_COMPANY_LIMIT).map(label).join(", ");
  const rest = rows.slice(NAMED_COMPANY_LIMIT);
  return (
    <>
      {shown} og{" "}
      <Hint text={rest.map(label).join(", ")}>
        {plural(rest.length, "bedrift til", "bedrifter til")}
      </Hint>
    </>
  );
}

/** "1. jan. 2026" — for saying out loud which days a number covers. */
function humanDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("nb-NO", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * The rule this dashboard follows, and the reason `Money`, `TotalCell`,
 * `MarginCell` and `KpiCard` all take a `formula`:
 *
 * **every computed number carries the arithmetic that produced it, with this
 * row's own figures in it.** A column heading says what a number *means*; the
 * formula says what it is *made of*, and that is the question a dashboard
 * actually gets asked — "where does 412 000 come from" is never answered by
 * "omsetning i perioden".
 *
 * A number is computed if it is a product, a sum, a quotient or a share. Plain
 * counts read straight off a table are left alone on purpose: "3 bedrifter"
 * explains itself, and underlining it too would teach readers to ignore the
 * dotted underline exactly where it matters.
 */
function Explain({
  children,
  formula,
}: {
  children: React.ReactNode;
  formula?: string;
}) {
  return (
    <Hint text={formula} className="decoration-admin-text-subtle/40">
      {children}
    </Hint>
  );
}

/** "3 200 kr × 14 leverte = 44 800 kr" — one line, real numbers, no prose. */
function times(rate: string, unit: string, n: number, result: string): string {
  return `${rate} × ${count(n)} ${unit} = ${result}`;
}

/** The rule down the left edge of a column group, in header and body alike. */
const GROUP_RULE = "border-l border-admin-card-border";

/** Comma-joined in the URL — `useDashboardUrlState` stores strings only. */
function parseList(value: string): string[] {
  return value ? value.split(",").filter(Boolean) : [];
}

type ProductRow = {
  flow_type: string;
  companies: number;
  companies_missing_income: number;
  /** One rate for the whole system, so this is a fact and not a count. */
  assessment_rate_missing: boolean;
  companies_ambiguous_sampling_price: number;
  ambiguous_draws: number;
  visits: number;
  undated_visits: number;
  outstanding_visits: number;
  outstanding_priced_visits: number;
  outstanding_income: string;
  outstanding_margin_visits: number;
  /** Of those, the ones priced off the product's average, not the company's. */
  outstanding_margin_product_visits: number;
  outstanding_margin_current: string;
  outstanding_margin_worst: string;
  priced_visits: number;
  income_total: string;
  sampling_cost: string;
  marker_cost: string;
  assessment_cost: string;
  cost_total: string;
  helfo_savings: string;
  private_markers: number;
  helfo_markers: number;
  unpriced_visits: number;
  unpriced_cost_total: string;
};

type CompanyRow = {
  company_id: string;
  company_name: string;
  flow_type: string;
  visits_per_year: number;
  employees: number;
  visits: number;
  undated_visits: number;
  outstanding_visits: number;
  cost_predictable: boolean;
  /**
   * Which average priced the backlog: the company's own all-time deliveries,
   * its product's, or neither. The page labels the middle one rather than
   * letting it pass for the company's own measurement.
   */
  cost_basis: "company" | "product" | null;
  /** Health checks the company has ever had delivered, at any date. */
  lifetime_visits: number;
  /** Health checks, and companies, behind the product average above. */
  product_visits: number;
  product_companies: number;
  /** The rate the backlog is actually priced at. Not the period's cost. */
  outstanding_cost_per_visit: string | null;
  outstanding_worst_cost_per_visit: string | null;
  outstanding_income: string | null;
  outstanding_margin_current: string | null;
  outstanding_margin_worst: string | null;
  assessment_rate_missing: boolean;
  income_per_employee: string | null;
  income_per_visit: string | null;
  assessment_rate: string;
  sampling_cost: string;
  marker_cost: string;
  assessment_cost: string;
  helfo_savings: string;
  private_markers: number;
  helfo_markers: number;
  cost_total: string;
  income_total: string | null;
  ambiguous_draws: number;
};

type MarkerRow = {
  biometric_name: string;
  private_count: number;
  helfo_count: number;
  private_cost: string;
  helfo_savings: string;
  unit_price: string | null;
  price_missing: boolean;
};

/** One lab that drew blood in the period, with the fee it charged. */
type SamplingRow = {
  lab_id: string;
  /** Null when the visit recorded no lab at all. */
  lab_name: string | null;
  furst_affiliated: boolean;
  /** Null when the lab has no price row, or several that are alternatives. */
  price: string | null;
  priced_on: string | null;
  price_rows: number;
  companies: number;
  visits: number;
  waived_visits: number;
  cost_total: string;
};

/** Companies sold at one price per health check, in the "Per bedrift" card. */
type PriceGroup = {
  price: number;
  companies: string[];
  visits: number;
  income: number;
  /** Share of the period's realized income, 0–100. */
  share: number;
};

const COMPANY_CSV_HEADERS = [
  "Bedrift",
  "Produkt",
  "Helsesjekker levert",
  "Inntekt per ansatt per ar",
  "Blodprover per ar",
  "Omsetning sa langt",
  "Kostnad sa langt",
  "Resultat sa langt",
  "Margin %",
  "Forventet omsetning",
  "Forventet resultat",
  "Omsetning per helsesjekk",
  "Kostnad per helsesjekk",
  "Margin per helsesjekk",
  "Provetaking per helsesjekk",
  "Biomarkorer per helsesjekk",
  "Legevurdering per helsesjekk",
  "Dekket av Helfo",
  "Utestaende helsesjekker (per na)",
  "Utestaende omsetning",
  "Utestaende margin uten Helfo",
  "Utestaende margin med dagens Helfo-andel",
  "Kostnad per utestaende helsesjekk",
  "Kostnadsgrunnlag",
];

/** The "produktsnitt" tag, for a reader who only has the CSV. */
const COST_BASIS_LABELS: Record<string, string> = {
  company: "Bedriftens eget snitt",
  product: "Produktets snitt",
};

/**
 * The two kroner totals beside the per-check figures.
 *
 * `soFar` is the period's realized margin — margin per check times the checks
 * delivered in it — so it moves with the date filter like everything else in
 * the row. `projected` adds the backlog: health checks that are ordered and
 * not yet drawn, valued at the same rates. Nothing beyond that is projected,
 * because nothing in the schema says how long an agreement runs.
 *
 * `projected` is null rather than optimistic when a backlog exists whose cost
 * cannot be known — the cost rate is the average of the checks delivered in
 * the period, so a company that delivered none has nothing to read it off,
 * and adding income alone would report a margin we have no basis for.
 */
function totalsFor(row: {
  income_total: string | null;
  cost_total: string;
  outstanding_visits: number;
  outstanding_margin_visits?: number;
  outstanding_margin_current: string | null;
}): { soFar: number | null; projected: number | null; partial: boolean } {
  if (row.income_total == null) return { soFar: null, projected: null, partial: false };
  const soFar = Number(row.income_total) - Number(row.cost_total);
  if (row.outstanding_visits === 0) return { soFar, projected: soFar, partial: false };
  if (row.outstanding_margin_current == null) {
    return { soFar, projected: null, partial: false };
  }
  const covered = row.outstanding_margin_visits ?? row.outstanding_visits;
  return {
    soFar,
    projected: soFar + Number(row.outstanding_margin_current),
    partial: covered < row.outstanding_visits,
  };
}

/**
 * One company row, in the two units the table shows: kroner in alt for the
 * period, and the same divided by the health checks it took to earn them.
 */
type CompanyPerVisit = {
  row: CompanyRow;
  visits: number;
  incomeTotal: number | null;
  costTotal: number | null;
  outstandingIncome: number;
  projectedIncome: number | null;
  income: number | null;
  sampling: number | null;
  markers: number | null;
  assessment: number | null;
  cost: number | null;
  margin: number | null;
  marginPct: number | null;
  totals: { soFar: number | null; projected: number | null; partial: boolean };
};

/**
 * Forventet resultat, spelled out — including *which* average priced the
 * backlog. "1 000 − 175" is only checkable if the 175 says where it came
 * from, and after the fallback it can come from two different places.
 */
function costBasisFormula(p: CompanyPerVisit): string | undefined {
  const r = p.row;
  if (p.totals.projected == null) return undefined;
  const head = `Så langt ${nok(p.totals.soFar)} + utestående margin ${nok(r.outstanding_margin_current)} = ${nok(p.totals.projected)} kr`;
  if (r.outstanding_visits === 0) return head;
  const rate = `${count(r.outstanding_visits)} bestilte × (${nok(r.income_per_visit)} kr omsetning − ${nok(r.outstanding_cost_per_visit)} kr kostnad) per helsesjekk`;
  const basis =
    r.cost_basis === "product"
      ? `Kostnaden er snittet over ${plural(r.product_visits, "helsesjekk", "helsesjekker")} levert av ${plural(r.product_companies, "bedrift", "bedrifter")} på samme produkt — ${r.company_name} har ikke levert noen selv`
      : `Kostnaden er snittet av alle ${plural(r.lifetime_visits, "helsesjekk", "helsesjekker")} ${r.company_name} har fått levert, uansett dato`;
  return `${head}. Utestående: ${rate}. ${basis}.`;
}

function perVisit(row: CompanyRow): CompanyPerVisit {
  const visits = row.visits;
  // A company with a backlog but no delivery in the period is kept by the
  // query on purpose. Its cost per health check is unknown, not 0 kr — and a
  // 0 here would read as full income at a 100 % margin.
  const div = (v: string) => (visits > 0 ? Number(v) / visits : null);
  const income = row.income_per_visit == null ? null : Number(row.income_per_visit);
  const cost = div(row.cost_total);
  const known = income != null && cost != null;
  const incomeTotal = row.income_total == null ? null : Number(row.income_total);
  // A company can have a backlog and no delivery in the period. Blanking
  // Forventet there would drop kroner the Utestående card still counts.
  const outstandingIncome = Number(row.outstanding_income ?? 0);
  return {
    row,
    visits,
    incomeTotal,
    // Known even when the rate is not: we paid the lab either way.
    costTotal: visits > 0 ? Number(row.cost_total) : null,
    outstandingIncome,
    projectedIncome:
      incomeTotal == null && outstandingIncome === 0
        ? null
        : (incomeTotal ?? 0) + outstandingIncome,
    income,
    sampling: div(row.sampling_cost),
    markers: div(row.marker_cost),
    assessment: div(row.assessment_cost),
    cost,
    margin: known ? income - cost : null,
    marginPct: known && income !== 0 ? ((income - cost) / income) * 100 : null,
    totals: totalsFor(row),
  };
}

// ---------------------------------------------------------------------------
// Table columns for DashboardTable — the label a column header shows (with
// its hover explanation) and the value a row carries in it, so the sort
// orders by the same figure the cell displays.
// ---------------------------------------------------------------------------

const HEAD = "whitespace-nowrap";
const HEAD_DIVIDE = cn(HEAD, GROUP_RULE);

/**
 * One product row, in the units the "Per produkt" table shows — the same
 * arithmetic the row used to do inline, lifted out so the sort can read the
 * values the cells display.
 */
function productRowTotals(r: ProductRow) {
  const v = r.priced_visits;
  const per = (value: string) => (v > 0 ? Number(value) / v : null);
  // Every money column is over the priced visits only, so with none of them
  // there is no amount to show — a 0 kr here would read as a product that
  // earned nothing.
  const incomeTotal = v > 0 ? Number(r.income_total) : null;
  const costTotal = v > 0 ? Number(r.cost_total) : null;
  // A product can have a backlog and nothing delivered yet. Blanking
  // Forventet there would drop kroner the Utestående card below still
  // counts, and the two would stop adding up.
  const outstandingIncome = Number(r.outstanding_income);
  const projectedIncome =
    incomeTotal == null && outstandingIncome === 0
      ? null
      : (incomeTotal ?? 0) + outstandingIncome;
  const totals = totalsFor(r);
  const result = v > 0 ? totals.soFar : null;
  const marginPct =
    incomeTotal && incomeTotal > 0 && result != null
      ? (result / incomeTotal) * 100
      : null;
  const avg = (total: number | null) =>
    total != null && v > 0 ? total / v : null;
  return {
    v,
    per,
    incomeTotal,
    costTotal,
    outstandingIncome,
    projectedIncome,
    totals,
    result,
    marginPct,
    avg,
  };
}

const PRODUCT_COLUMNS: DashboardTableColumn<ProductRow>[] = [
  {
    id: "flow_type",
    label: "Produkt",
    value: (r) => FLOW_LABELS[r.flow_type] ?? r.flow_type,
    headClassName: HEAD,
  },
  {
    id: "companies",
    label: (
      <Hint text="Bedrifter med minst én levert helsesjekk i perioden, eller minst én bestilt som ikke er tatt ennå.">
        Bedrifter
      </Hint>
    ),
    value: (r) => r.companies,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "priced_visits",
    label: (
      <Hint text="Leverte helsesjekker i perioden. Det er dette tallet småtekstene under beløpene er delt på. Et gult +N er leverte helsesjekker hos bedrifter uten inntekt per ansatt — de mangler en pris, så de er ikke med i noen av beløpene.">
        Helsesjekker
      </Hint>
    ),
    value: (r) => r.priced_visits,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "income_total",
    label: (
      <Hint text="Omsetningen på helsesjekkene som ble levert i perioden: bedriftens inntekt per ansatt delt på blodprøver per år, ganget med antall leverte. Småteksten er det samme per helsesjekk.">
        Omsetning
      </Hint>
    ),
    value: (r) => productRowTotals(r).incomeTotal,
    numeric: true,
    align: "right",
    headClassName: HEAD_DIVIDE,
  },
  {
    id: "cost_total",
    label: (
      <Hint text="Hva de samme helsesjekkene kostet oss: prøvetaking + biomarkører + legevurdering. Småteksten er det samme per helsesjekk, og de tre kolonnene helt til høyre viser hvordan den fordeler seg.">
        Kostnad
      </Hint>
    ),
    value: (r) => productRowTotals(r).costTotal,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "result",
    label: (
      <Hint text="Omsetning minus kostnad — det vi sitter igjen med i perioden. Småteksten er det samme per helsesjekk.">
        Resultat
      </Hint>
    ),
    value: (r) => productRowTotals(r).result,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "margin_pct",
    label: (
      <Hint text="Resultat delt på omsetning. Den samme uansett hvor mange helsesjekker som leveres, siden begge vokser i takt — derfor projiseres den ikke.">
        Margin %
      </Hint>
    ),
    value: (r) => productRowTotals(r).marginPct,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "projected_income",
    label: (
      <Hint text="Omsetningen så langt pluss de bestilte helsesjekkene som ikke er tatt ennå, til samme satser. Småteksten «herav … utestående» er det andre leddet, og er den samme summen som «Utestående»-kortene under tabellen viser. Vi projiserer ikke lenger enn det som faktisk er bestilt — ingenting i systemet sier hvor lenge en avtale varer.">
        Omsetning
      </Hint>
    ),
    value: (r) => productRowTotals(r).projectedIncome,
    numeric: true,
    align: "right",
    headClassName: HEAD_DIVIDE,
  },
  {
    id: "projected_result",
    label: (
      <Hint text="Resultatet så langt pluss de bestilte helsesjekkene som ikke er tatt ennå. Kostnaden per helsesjekk er snittet av alt bedriften noen gang har fått levert, med den Helfo-andelen den faktisk hadde — har den aldri levert noe, brukes produktets eget snitt. Et grått +N? er bestilte helsesjekker som ikke er med i det hele tatt: de er hos bedrifter uten inntekt per ansatt, eller der verken bedriften eller produktet har levert én eneste helsesjekk å regne et snitt av. Hold musepekeren over tallet for å se fordelingen.">
        Resultat
      </Hint>
    ),
    // Same guard as the cell: with no priced visit there is no result to
    // project from, so a blank cell must sort last, not among real results.
    value: (r) => {
      const t = productRowTotals(r);
      return t.result == null ? null : t.totals.projected;
    },
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "sampling_cost",
    label: (
      <Hint text="Prøvetakingsgebyret draw-stedet tar (lab_prices), delt på antall leverte helsesjekker. Null når laben er Furst-tilknyttet og prøven gikk på Helfo.">
        Prøvetaking
      </Hint>
    ),
    value: (r) => productRowTotals(r).avg(Number(r.sampling_cost)),
    numeric: true,
    align: "right",
    headClassName: HEAD_DIVIDE,
  },
  {
    id: "marker_cost",
    label: (
      <Hint text="Det Furst tar for markørene på privat rekvisisjon (biometric_prices), delt på antall leverte helsesjekker. Markører Helfo dekker koster oss ingenting og er ikke med.">
        Biomarkører
      </Hint>
    ),
    value: (r) => productRowTotals(r).avg(Number(r.marker_cost)),
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "assessment_cost",
    label: (
      <Hint text="Hva det koster oss at en lege skriver vurderingen for én helsesjekk. Én sats for alle kunder, satt i Katalog → Priser.">
        Legevurdering
      </Hint>
    ),
    value: (r) => productRowTotals(r).avg(Number(r.assessment_cost)),
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
];

const PRODUCT_HEADER_ROWS: DashboardTableHeaderRow[] = [
  {
    cells: [
      { colSpan: 3 },
      { text: "Så langt", colSpan: 4, divide: true },
      {
        text: (
          <>
            Forventet{" "}
            <span className="normal-case font-normal">
              (så langt + utestående)
            </span>
          </>
        ),
        colSpan: 2,
        divide: true,
      },
      { text: "Kostnad per helsesjekk", colSpan: 3, divide: true },
    ],
  },
];

const COMPANY_COLUMNS: DashboardTableColumn<CompanyPerVisit>[] = [
  {
    id: "company_name",
    label: "Bedrift",
    value: (p) => p.row.company_name,
    headClassName: HEAD,
  },
  {
    id: "flow_type",
    label: "Produkt",
    value: (p) => FLOW_LABELS[p.row.flow_type] ?? p.row.flow_type,
    headClassName: HEAD,
  },
  {
    id: "visits",
    label: (
      <Hint text="Leverte helsesjekker i perioden. Det er dette tallet småtekstene under beløpene er delt på.">
        Helsesjekker
      </Hint>
    ),
    value: (p) => p.visits,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "income_total",
    label: (
      <Hint text="Omsetningen på helsesjekkene bedriften fikk levert i perioden: inntekt per ansatt delt på blodprøver per år, ganget med antall leverte. Småteksten er det samme per helsesjekk. «mangler» betyr at ingen har lagt inn satsen — omsetningen er ukjent, ikke null.">
        Omsetning
      </Hint>
    ),
    value: (p) => p.incomeTotal,
    numeric: true,
    align: "right",
    headClassName: HEAD_DIVIDE,
  },
  {
    id: "cost_total",
    label: (
      <Hint text="Hva de samme helsesjekkene kostet oss: prøvetaking + biomarkører + legevurdering. Kjent også når satsen mangler — vi betalte laben uansett. Småteksten er det samme per helsesjekk.">
        Kostnad
      </Hint>
    ),
    value: (p) => p.costTotal,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "result",
    label: (
      <Hint text="Omsetning minus kostnad — det vi sitter igjen med på bedriften i perioden. Småteksten er det samme per helsesjekk.">
        Resultat
      </Hint>
    ),
    value: (p) => p.totals.soFar,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "margin_pct",
    label: (
      <Hint text="Resultat delt på omsetning. Den samme uansett hvor mange helsesjekker bedriften tar, siden begge vokser i takt.">
        Margin %
      </Hint>
    ),
    value: (p) => p.marginPct,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "projected_income",
    label: (
      <Hint text="Omsetningen så langt pluss bedriftens bestilte helsesjekker som ikke er tatt ennå, til bedriftens egen sats. Småteksten «herav … utestående» er det andre leddet — samme tall som Utestående-kolonnen helt til høyre.">
        Omsetning
      </Hint>
    ),
    value: (p) => p.projectedIncome,
    numeric: true,
    align: "right",
    headClassName: HEAD_DIVIDE,
  },
  {
    id: "projected_result",
    label: (
      <Hint text="Resultatet så langt pluss de bestilte helsesjekkene som ikke er tatt ennå, til bedriftens egne satser. Kostnaden per helsesjekk er snittet av alle helsesjekker bedriften noen gang har fått levert — ikke bare periodens, for køen telles per nå — med den Helfo-andelen den faktisk hadde. Har bedriften aldri levert noen, brukes produktets eget snitt, og tallet er merket «produktsnitt». «ukjent» betyr at det ikke er levert én eneste helsesjekk på hele produktet å regne et snitt av.">
        Resultat
      </Hint>
    ),
    value: (p) => p.totals.projected,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "sampling_cost",
    label: (
      <Hint text="Prøvetakingsgebyret draw-stedet tar (lab_prices), delt på antall leverte helsesjekker.">
        Prøvetaking
      </Hint>
    ),
    value: (p) => p.sampling,
    numeric: true,
    align: "right",
    headClassName: HEAD_DIVIDE,
  },
  {
    id: "marker_cost",
    label: (
      <Hint text="Furst-prisen på markørene vi betalte selv, delt på antall leverte helsesjekker. Markører Helfo dekker er ikke med.">
        Biomarkører
      </Hint>
    ),
    value: (p) => p.markers,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "assessment_cost",
    label: (
      <Hint text="Hva det koster oss at en lege skriver vurderingen for én helsesjekk. Én sats for alle kunder, satt i Katalog → Priser.">
        Legevurdering
      </Hint>
    ),
    value: (p) => p.assessment,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "outstanding_visits",
    label: (
      <Hint text="Bestilte helsesjekker som ikke er tatt ennå: antall, og hva de er verdt i omsetning til bedriftens egen sats. Telt per nå, ikke i perioden — derfor følger den ikke datofilteret.">
        Utestående
      </Hint>
    ),
    value: (p) => p.row.outstanding_visits,
    numeric: true,
    align: "right",
    headClassName: HEAD_DIVIDE,
  },
];

const COMPANY_HEADER_ROWS: DashboardTableHeaderRow[] = [
  {
    cells: [
      { colSpan: 3 },
      { text: "Så langt", colSpan: 4, divide: true },
      {
        text: (
          <>
            Forventet{" "}
            <span className="normal-case font-normal">
              (så langt + utestående)
            </span>
          </>
        ),
        colSpan: 2,
        divide: true,
      },
      { text: "Kostnad per helsesjekk", colSpan: 3, divide: true },
      { colSpan: 1, divide: true },
    ],
  },
];

const PRICE_GROUP_COLUMNS: DashboardTableColumn<PriceGroup>[] = [
  {
    id: "price",
    label: (
      <Hint text="Inntekt per ansatt delt på blodprøver per år. Bedrifter som betaler det samme er slått sammen — hold over antallet for å se hvilke.">
        Pris per helsesjekk
      </Hint>
    ),
    value: (g) => g.price,
    numeric: true,
    headClassName: HEAD,
  },
  {
    id: "companies",
    label: (
      <Hint text="Bedrifter som betaler nøyaktig denne prisen per helsesjekk.">
        Bedrifter
      </Hint>
    ),
    value: (g) => g.companies.length,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "visits",
    label: (
      <Hint text="Helsesjekker levert til denne prisen i perioden.">
        Helsesjekker
      </Hint>
    ),
    value: (g) => g.visits,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "income",
    label: (
      <Hint text="Pris ganget med antall leverte helsesjekker.">Omsetning</Hint>
    ),
    value: (g) => g.income,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "share",
    label: (
      <Hint text="Hvor stor del av all realisert omsetning i perioden denne prisen står for.">
        Andel
      </Hint>
    ),
    value: (g) => g.share,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
];

const SAMPLING_COLUMNS: DashboardTableColumn<SamplingRow>[] = [
  {
    id: "lab_name",
    label: (
      <Hint text="Laben blodprøven ble tatt på (lab_visits.lab_id).">Lab</Hint>
    ),
    value: (r) => r.lab_name,
    headClassName: HEAD,
  },
  {
    id: "price",
    label: (
      <Hint text="Prisen laben tar per blodprøve (lab_prices). Har laben flere priser er de alternativer for ulike kunder, og da settes ingen pris.">
        Pris
      </Hint>
    ),
    value: (r) => (r.price == null ? null : Number(r.price)),
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "priced_on",
    label: (
      <Hint text="Når prisen sist ble endret (lab_prices.updated_at). En gammel dato på en lab vi bruker mye er verdt et blikk.">
        Sist endret
      </Hint>
    ),
    value: (r) => r.priced_on,
    headClassName: HEAD,
  },
  {
    id: "companies",
    label: (
      <Hint text="Bedrifter som tok blodprøver på denne laben i perioden.">
        Bedrifter
      </Hint>
    ),
    value: (r) => r.companies,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "paid_visits",
    label: (
      <Hint text="Blodprøver i perioden vi betalte for. Gratis-tallet er prøver som gikk på Helfo hos en Furst-tilknyttet lab — der tar laben ingenting.">
        Betalte prøver
      </Hint>
    ),
    value: (r) => r.visits - r.waived_visits,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "cost_total",
    label: (
      <Hint text="Prisen ganget med antall betalte blodprøver.">Kostnad</Hint>
    ),
    value: (r) => Number(r.cost_total),
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
];

const MARKER_COLUMNS: DashboardTableColumn<MarkerRow>[] = [
  {
    id: "biometric_name",
    label: (
      <Hint text="Én rad = én Furst-analysekode. Flere katalogmarkører kan dele kode og faktureres da én gang; raden vises under den dyreste av dem.">
        Markør
      </Hint>
    ),
    value: (r) => r.biometric_name,
    headClassName: HEAD,
  },
  {
    id: "unit_price",
    label: (
      <Hint text="Høyeste avtalepris sett for denne Furst-koden i perioden.">
        Enhetspris
      </Hint>
    ),
    value: (r) => (r.unit_price == null ? null : Number(r.unit_price)),
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "private_count",
    label: (
      <Hint text="Ganger markøren lå på en privat rekvisisjon på en levert helsesjekk i perioden. Vi betalte den.">
        Antall privat
      </Hint>
    ),
    value: (r) => r.private_count,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "private_cost",
    label: (
      <Hint text="Summen vi betalte for markøren i perioden.">
        Kostnad privat
      </Hint>
    ),
    value: (r) => Number(r.private_cost),
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "helfo_count",
    label: (
      <Hint text="Ganger markøren lå på en Helfo-rekvisisjon. Staten betalte den.">
        Antall Helfo
      </Hint>
    ),
    value: (r) => r.helfo_count,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "helfo_savings",
    label: (
      <Hint text="Hva Helfo-markørene ville kostet oss til avtalepris — kostnad vi slapp, ikke omsetning.">
        Verdi Helfo
      </Hint>
    ),
    value: (r) => Number(r.helfo_savings),
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
  {
    id: "helfo_share",
    label: (
      <Hint text="Andel Helfo av alle gangene markøren ble tatt.">
        Andel Helfo
      </Hint>
    ),
    value: (r) =>
      r.helfo_count > 0
        ? (r.helfo_count / (r.private_count + r.helfo_count)) * 100
        : null,
    numeric: true,
    align: "right",
    headClassName: HEAD,
  },
];

export function B2bEconomicsDashboard() {
  const [{ from, to, companyIds, flowTypes }, update] = useDashboardUrlState({
    from: `${new Date().getFullYear()}-01-01`,
    to: isoDate(new Date()),
    companyIds: "",
    flowTypes: "",
  });

  const { data: companies } = useCompanies();

  const selectedFlows = useMemo(() => parseList(flowTypes), [flowTypes]);
  const selectedCompanies = useMemo(() => parseList(companyIds), [companyIds]);

  // Company options track the product filter, which is Julie's "filtrer bedrift
  // basert på valgt produkt". Deactivated companies stay listed: they still
  // have delivered health checks in past periods.
  const companyOptions = useMemo(() => {
    const rows = companies ?? [];
    const inScope = selectedFlows.length
      ? rows.filter((c) => selectedFlows.includes(c.flowType ?? ""))
      : rows;
    return inScope.map((c) => ({ value: c.id, label: c.name }));
  }, [companies, selectedFlows]);

  // Narrowing the product filter can strip a company that is still selected,
  // which would silently filter the whole page to a company the user can no
  // longer see. Drop those from the selection instead.
  useEffect(() => {
    if (selectedCompanies.length === 0 || companyOptions.length === 0) return;
    const allowed = new Set(companyOptions.map((o) => o.value));
    const kept = selectedCompanies.filter((id) => allowed.has(id));
    if (kept.length !== selectedCompanies.length) {
      update({ companyIds: kept.join(",") });
    }
  }, [companyOptions, selectedCompanies, update]);

  const params = useMemo(
    () => ({
      from,
      to,
      ...(selectedFlows.length ? { flowTypes: selectedFlows } : {}),
      ...(selectedCompanies.length ? { companyIds: selectedCompanies } : {}),
    }),
    [from, to, selectedFlows, selectedCompanies],
  );

  const products = useDashboardChart<typeof params, ProductRow>(
    DASHBOARD_ID,
    "product-economics",
    params,
  );
  const companyRows = useDashboardChart<typeof params, CompanyRow>(
    DASHBOARD_ID,
    "company-economics",
    params,
  );
  const samplingRates = useDashboardChart<typeof params, SamplingRow>(
    DASHBOARD_ID,
    "sampling-rates",
    params,
  );
  const markers = useDashboardChart<typeof params, MarkerRow>(
    DASHBOARD_ID,
    "marker-usage",
    params,
  );

  /**
   * Prøvetakning is the one cost on the page a reader cannot check anywhere
   * else: it comes from lab_prices rows nobody sees, and a lab carries one
   * per customer arrangement. Summing them would charge a draw every deal on
   * the price list at once, so the rows stay separate — the total is a sum of
   * kroner, never of rates.
   */
  const sampling = useMemo(() => {
    const rows = samplingRates.data;
    if (!rows) return null;
    const total = rows.reduce((a, r) => a + Number(r.cost_total), 0);
    // Only labs carrying several prices. A lab with none does not bill us,
    // so its 0 is the answer and nothing to warn about.
    const ambiguous = rows
      .filter((r) => r.price_rows > 1)
      .reduce((a, r) => a + r.visits, 0);
    return { rows, total, ambiguous };
  }, [samplingRates.data]);

  /**
   * The breakdown is only worth trusting if it closes. Every line summed is
   * the prøvetakning the per-bedrift table divides by visits, so the page can
   * check itself instead of asking the reader to take it on faith.
   */
  const samplingCheck = useMemo(() => {
    if (!sampling || !companyRows.data) return null;
    const onPage = companyRows.data.reduce(
      (a, r) => a + Number(r.sampling_cost),
      0,
    );
    return { onPage, diff: sampling.total - onPage };
  }, [sampling, companyRows.data]);

  const scopeLabel = useMemo(() => {
    if (selectedCompanies.length === 1) {
      return companyOptions.find((o) => o.value === selectedCompanies[0])?.label ?? null;
    }
    if (selectedCompanies.length > 1) return `${selectedCompanies.length} bedrifter`;
    return null;
  }, [selectedCompanies, companyOptions]);

  /** Page-level totals, summed from the product rows so they cannot drift. */
  const totals = useMemo(() => {
    const rows = products.data;
    if (!rows || rows.length === 0) return null;
    const sum = (pick: (r: ProductRow) => number) =>
      rows.reduce((acc, r) => acc + pick(r), 0);

    const visits = sum((r) => r.priced_visits);
    const income = sum((r) => Number(r.income_total));
    const cost = sum((r) => Number(r.cost_total));
    const per = (v: number) => (visits > 0 ? v / visits : 0);

    return {
      visits,
      deliveredVisits: sum((r) => r.visits),
      income,
      cost,
      helfo: sum((r) => Number(r.helfo_savings)),
      sampling: sum((r) => Number(r.sampling_cost)),
      markerCost: sum((r) => Number(r.marker_cost)),
      assessment: sum((r) => Number(r.assessment_cost)),
      privateMarkers: sum((r) => r.private_markers),
      helfoMarkers: sum((r) => r.helfo_markers),
      incomePerVisit: per(income),
      costPerVisit: per(cost),
      helfoPerVisit: per(sum((r) => Number(r.helfo_savings))),
      marginPerVisit: per(income - cost),
      marginPct: income > 0 ? ((income - cost) / income) * 100 : null,
      missingIncome: sum((r) => r.companies_missing_income),
      assessmentRateMissing: rows.some((r) => r.assessment_rate_missing),
      ambiguousSampling: sum((r) => r.companies_ambiguous_sampling_price),
      ambiguousDraws: sum((r) => r.ambiguous_draws),
      unpricedVisits: sum((r) => r.unpriced_visits),
      unpricedCost: sum((r) => Number(r.unpriced_cost_total)),
      undatedVisits: sum((r) => r.undated_visits),
      outstandingVisits: sum((r) => r.outstanding_visits),
      outstandingPricedVisits: sum((r) => r.outstanding_priced_visits),
      outstandingIncome: sum((r) => Number(r.outstanding_income)),
      outstandingMarginVisits: sum((r) => r.outstanding_margin_visits),
      outstandingMarginProductVisits: sum(
        (r) => r.outstanding_margin_product_visits,
      ),
      outstandingMarginCurrent: sum((r) => Number(r.outstanding_margin_current)),
      outstandingMarginWorst: sum((r) => Number(r.outstanding_margin_worst)),
      // Share of what markers would have cost us that Helfo actually covered.
      // The assumption the "current" case carries forward, stated out loud.
      helfoShare: (() => {
        const helfo = sum((r) => Number(r.helfo_savings));
        const gross = helfo + sum((r) => Number(r.marker_cost));
        return gross > 0 ? (helfo / gross) * 100 : null;
      })(),
    };
  }, [products.data]);

  /**
   * Who the Utestående card leaves out, and who it estimates. Derived from the
   * per-company rows rather than from the product totals so the count and the
   * names can never disagree — and named, because at these sizes a name is
   * actionable ("enter this contract") where a bare count is only unsettling.
   */
  const outstandingGaps = useMemo(() => {
    const rows = companyRows.data;
    if (!rows) return null;
    const pending = rows.filter((r) => r.outstanding_visits > 0);
    return {
      // No contract rate: neither side of the margin is knowable.
      noRate: pending.filter((r) => r.income_per_visit == null),
      // Rate but no cost anywhere, not even at product level.
      noCost: pending.filter(
        (r) => r.income_per_visit != null && r.cost_basis == null,
      ),
      // Costed off the product's average because the company itself has never
      // had a health check delivered. In the numbers, but the weaker estimate.
      borrowed: pending.filter(
        (r) => r.income_per_visit != null && r.cost_basis === "product",
      ),
    };
  }, [companyRows.data]);

  /**
   * What one health check is sold for, grouped by price. Julie's "hvor stor
   * del av kaken hver pris utgjør" — with several companies on the same
   * product at different rates, the average alone hides the spread.
   */
  const priceGroups = useMemo(() => {
    const rows = companyRows.data;
    if (!rows) return null;
    const priced = rows.map(perVisit).filter((p) => p.income != null && p.visits > 0);
    if (priced.length === 0) return null;

    const totalIncome = priced.reduce((a, p) => a + p.income! * p.visits, 0);
    const totalVisits = priced.reduce((a, p) => a + p.visits, 0);

    const byPrice = new Map<number, { companies: string[]; visits: number; income: number }>();
    for (const p of priced) {
      const key = Math.round(p.income! * 100) / 100;
      const g = byPrice.get(key) ?? { companies: [], visits: 0, income: 0 };
      g.companies.push(p.row.company_name);
      g.visits += p.visits;
      g.income += p.income! * p.visits;
      byPrice.set(key, g);
    }

    // Median over health checks, not over companies: a customer with 200
    // delivered checks should move it and a customer with one should not.
    const spread = priced
      .flatMap((p) => Array<number>(p.visits).fill(p.income!))
      .sort((a, b) => a - b);
    const mid = Math.floor(spread.length / 2);
    const median =
      spread.length % 2 === 0 ? (spread[mid - 1]! + spread[mid]!) / 2 : spread[mid]!;

    const groups: PriceGroup[] = [...byPrice.entries()]
      .map(([price, g]) => ({ price, ...g, share: (g.income / totalIncome) * 100 }))
      .sort((a, b) => b.income - a.income);

    return {
      groups,
      // The bar's shade identifies a price, so the table's dot keeps the bar's
      // colour no matter how the rows are sorted.
      priceIndex: new Map(groups.map((g, i) => [g.price, i])),
      average: totalIncome / totalVisits,
      median,
      totalIncome,
    };
  }, [companyRows.data]);

  const productChartData = useMemo(() => {
    const rows = products.data;
    if (!rows) return [];
    return rows
      .filter((r) => r.priced_visits > 0)
      .map((r) => {
        const v = r.priced_visits;
        const income = Number(r.income_total) / v;
        const cost = Number(r.cost_total) / v;
        return {
          name: FLOW_LABELS[r.flow_type] ?? r.flow_type,
          omsetning: income,
          sampling: Number(r.sampling_cost) / v,
          markers: Number(r.marker_cost) / v,
          assessment: Number(r.assessment_cost) / v,
          margin: income - cost,
          marginPct: income > 0 ? ((income - cost) / income) * 100 : null,
        };
      });
  }, [products.data]);

  const markerChartData = useMemo(() => {
    const rows = markers.data;
    if (!rows) return [];
    return rows.slice(0, MARKER_CHART_ROWS).map((r) => ({
      name: r.biometric_name,
      privat: r.private_count,
      helfo: r.helfo_count,
      total: r.private_count + r.helfo_count,
    }));
  }, [markers.data]);

  // Sort and column filters for the five tables, in the URL. The company
  // table's rows are pre-shaped with perVisit, so its export below follows
  // the table's own view.
  const productTable = useDashboardTable(
    "product-economics",
    PRODUCT_COLUMNS,
    products.data,
  );
  const priceGroupTable = useDashboardTable(
    "price-groups",
    PRICE_GROUP_COLUMNS,
    priceGroups?.groups ?? null,
  );
  const companyPerVisit = useMemo(
    () => (companyRows.data ? companyRows.data.map(perVisit) : null),
    [companyRows.data],
  );
  const companyTable = useDashboardTable(
    "company-economics",
    COMPANY_COLUMNS,
    companyPerVisit,
  );
  const samplingTable = useDashboardTable(
    "sampling-rates",
    SAMPLING_COLUMNS,
    sampling?.rows ?? null,
  );
  const markerTable = useDashboardTable(
    "marker-usage",
    MARKER_COLUMNS,
    markers.data,
  );

  const exportCompanies = () => {
    const rows = companyTable.viewRows;
    if (rows.length === 0) return;
    const csv = toCsv(
      COMPANY_CSV_HEADERS,
      rows.map((p) => [
        p.row.company_name,
        FLOW_LABELS[p.row.flow_type] ?? p.row.flow_type,
        p.visits,
        csvDecimal(p.row.income_per_employee),
        p.row.visits_per_year,
        csvDecimal(p.incomeTotal?.toFixed(2)),
        csvDecimal(p.costTotal?.toFixed(2)),
        csvDecimal(p.totals.soFar?.toFixed(2)),
        csvDecimal(p.marginPct?.toFixed(1)),
        csvDecimal(p.projectedIncome?.toFixed(2)),
        csvDecimal(p.totals.projected?.toFixed(2)),
        csvDecimal(p.income?.toFixed(2)),
        csvDecimal(p.cost?.toFixed(2)),
        csvDecimal(p.margin?.toFixed(2)),
        csvDecimal(p.sampling?.toFixed(2)),
        csvDecimal(p.markers?.toFixed(2)),
        csvDecimal(p.assessment?.toFixed(2)),
        csvDecimal(p.row.helfo_savings),
        p.row.outstanding_visits,
        csvDecimal(p.row.outstanding_income),
        csvDecimal(p.row.outstanding_margin_worst),
        csvDecimal(p.row.outstanding_margin_current),
        csvDecimal(p.row.outstanding_cost_per_visit),
        (p.row.cost_basis && COST_BASIS_LABELS[p.row.cost_basis]) || "Ukjent",
      ]),
      CSV_DELIMITER,
    );
    const scope = scopeLabel ? filenameSlug(scopeLabel) : "alle-bedrifter";
    downloadCsv(csv, `b2b-lonnsomhet-${scope}-${from}-${to}.csv`, { bom: true });
    toast.success(`${rows.length} bedrifter eksportert`);
  };

  return (
    <div className="space-y-6">
      <FiltersCard>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">
              Blodprøve tatt fra
            </span>
            <Input
              type="date"
              value={from}
              onChange={(e) => update({ from: e.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">
              Blodprøve tatt til
            </span>
            <Input
              type="date"
              value={to}
              onChange={(e) => update({ to: e.target.value })}
              className="w-44"
            />
          </label>
          <div className="space-y-1 min-w-[13rem]">
            <span className="text-xs text-admin-text-subtle">Produkt</span>
            <MultiSelect
              options={Object.entries(FLOW_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
              selected={selectedFlows}
              onChange={(next) => update({ flowTypes: next.join(",") })}
              placeholder="Alle produkter"
              searchPlaceholder="Søk produkt…"
            />
          </div>
          <div className="space-y-1 flex-1 min-w-[15rem]">
            <span className="text-xs text-admin-text-subtle">Bedrift</span>
            <MultiSelect
              options={companyOptions}
              selected={selectedCompanies}
              onChange={(next) => update({ companyIds: next.join(",") })}
              placeholder="Alle bedrifter"
              searchPlaceholder="Søk bedrift…"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {PERIOD_PRESETS.map((p) => (
            <Button
              key={p.label}
              variant={from === p.from && to === p.to ? "default" : "outline"}
              size="sm"
              onClick={() => update({ from: p.from, to: p.to })}
            >
              {p.label}
            </Button>
          ))}
        </div>
        {/* The one thing a reader has to know before trusting any number on
            the page: which date the period is measured on. */}
        <p className="text-xs text-admin-text-subtle">
          Perioden filtrerer på <span className="text-admin-text-secondary">
          når blodprøven ble tatt</span> (lab_visits.visit_date), ikke på når
          kunden ble kunde eller når besøket ble opprettet. Alt på siden
          gjelder helsesjekker tatt{" "}
          <span className="text-admin-text-secondary">
            {humanDate(from)} – {humanDate(to)}
          </span>
          , unntatt «Utestående», som telles per nå. Markører er priset til
          avtaleprisen vi faktisk betaler.
        </p>
        <p className="text-xs text-admin-text-subtle">
          <span className="text-admin-text-secondary">Omsetning</span> er hva
          kunden betaler oss, <span className="text-admin-text-secondary">
          kostnad</span> hva helsesjekken koster oss, og{" "}
          <span className="text-admin-text-secondary">resultat</span>{" "}
          differansen. «Inntekt per ansatt» er avtalens sats, ikke kroner vi
          har tjent — den blir omsetning først når en helsesjekk er levert.
        </p>
      </FiltersCard>

      {totals && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              label="Omsetning"
              value={`${nok(totals.income)} kr`}
              hint={`${count(totals.visits)} helsesjekker × ${nok(totals.incomePerVisit)} kr i snitt`}
              color={COLOR.income}
              explain={`Omsetningen for helsesjekkene som ble tatt ${humanDate(from)} – ${humanDate(to)}. Per bedrift: inntekt per ansatt delt på blodprøver per år, ganget med antall leverte helsesjekker. Bedrifter uten inntekt per ansatt er ikke med.`}
              formula={times(
                `${nok(totals.incomePerVisit)} kr i snitt per helsesjekk`,
                "leverte helsesjekker",
                totals.visits,
                `${nok(totals.income)} kr`,
              )}
            />
            <KpiCard
              label="Kostnad"
              value={`${nok(totals.cost)} kr`}
              hint={`${nok(totals.costPerVisit)} kr per helsesjekk`}
              explain="Prøvetaking (lab_prices) + biomarkører (biometric_prices, avtaleprisen vi betaler) + legevurdering (én sats for alle, fra prices), for de samme helsesjekkene. Markører Helfo dekker koster oss ingenting og er ikke med her."
              formula={`Prøvetaking ${nok(totals.sampling)} + biomarkører ${nok(totals.markerCost)} + legevurdering ${nok(totals.assessment)} = ${nok(totals.cost)} kr, fordelt på ${count(totals.visits)} leverte helsesjekker = ${nok(totals.costPerVisit)} kr per stk`}
            />
            <KpiCard
              label="Margin"
              value={`${nok(totals.income - totals.cost)} kr`}
              hint={`${pct(totals.marginPct)} · ${nok(totals.marginPerVisit)} kr per helsesjekk`}
              color={
                totals.marginPct == null
                  ? undefined
                  : marginColor(totals.marginPct)
              }
              explain="(Omsetning − kostnad) delt på omsetning, over de samme helsesjekkene. Fordi begge er per helsesjekk, er dette den samme prosenten uansett hvor mange som leveres — derfor projiseres den ikke."
              formula={`Omsetning ${nok(totals.income)} − kostnad ${nok(totals.cost)} = ${nok(totals.income - totals.cost)} kr. Delt på omsetningen: ${pct(totals.marginPct)}`}
            />
            <KpiCard
              label="Dekket av Helfo"
              value={`${nok(totals.helfo)} kr`}
              hint={`${nok(totals.helfoPerVisit)} kr per helsesjekk spart`}
              color={COLOR.helfo}
              explain="Hva markørene på Helfo-rekvisisjonene ville kostet oss til vår egen avtalepris, hadde vi tatt dem selv. Staten betaler dem, så dette er kostnad vi slapp — verken omsetning eller kostnad i tallene til venstre."
              formula={`${count(totals.helfoMarkers)} markører på Helfo-rekvisisjon, til avtaleprisen vi selv betaler = ${nok(totals.helfo)} kr. Til sammenligning betalte vi selv for ${count(totals.privateMarkers)} markører (${nok(totals.markerCost)} kr)`}
            />
          </div>
          {/* One line rather than four more cards: these qualify the numbers
              above, they are not numbers anyone steers by. */}
          <p className="text-xs text-admin-text-subtle">
            {plural(totals.deliveredVisits, "helsesjekk", "helsesjekker")}{" "}
            levert i perioden{scopeLabel ? ` · ${scopeLabel}` : ""}
            {totals.unpricedVisits > 0 && (
              <span className="text-admin-warn-400">
                {" "}
                · {count(totals.unpricedVisits)} av dem ({nok(totals.unpricedCost)}{" "}
                kr i kostnad) er holdt utenfor tallene over fordi{" "}
                {plural(totals.missingIncome, "bedrift", "bedrifter")} mangler
                inntekt per ansatt
              </span>
            )}
            {totals.assessmentRateMissing && (
              <span className="text-admin-warn-400">
                {" "}
                · ingen sats for legevurdering er lagt inn, så den teller som 0
                kr — sett den i Katalog → Priser
              </span>
            )}
            {totals.ambiguousSampling > 0 && (
              <span className="text-admin-warn-400">
                {" "}
                · {plural(totals.ambiguousSampling, "bedrift", "bedrifter")}{" "}
                tok blodprøver på en lab med flere priser, så{" "}
                {plural(totals.ambiguousDraws, "blodprøve", "blodprøver")} er
                regnet som 0 kr i prøvetakning
              </span>
            )}
            {totals.undatedVisits > 0 && (
              <span className="text-admin-warn-400">
                {" "}
                · {count(totals.undatedVisits)} mangler dato for prøvetaking og
                er datert på når besøket ble opprettet
              </span>
            )}
          </p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Per produkt — én helsesjekk</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ChartState query={products} empty="Ingen leverte helsesjekker i perioden.">
            {() => (
              <>
                <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
                  {/* Hand-rolled rather than Recharts' <Legend>, which orders
                      itself by stack and drops Omsetning into the middle of the
                      cost ramp. */}
                  <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-admin-text-secondary">
                    {[
                      ["Omsetning", COLOR.income],
                      ["Prøvetaking", COLOR.sampling],
                      ["Biomarkører", COLOR.markers],
                      ["Legevurdering", COLOR.assessment],
                    ].map(([label, color]) => (
                      <span key={label} className="flex items-center gap-1.5">
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-sm"
                          style={{ backgroundColor: color }}
                        />
                        {label}
                      </span>
                    ))}
                  </div>
                  <ResponsiveContainer width="100%" height={290}>
                    <BarChart
                      data={productChartData}
                      margin={{ top: 24, right: 16, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                      <XAxis dataKey="name" tick={axisStyle} />
                      <YAxis
                        tick={axisStyle}
                        width={64}
                        tickFormatter={(v: number) => v.toLocaleString("no-NO")}
                      />
                      <Tooltip content={<PerVisitTooltip />} />
                      <Bar dataKey="omsetning" name="Omsetning" fill={COLOR.income}>
                        {/* The number over a bar is that bar. This one is the
                            income column, so it says the income — the margin
                            is the gap down to the cost stack, and lives in the
                            tooltip and the Resultat column below. */}
                        <LabelList
                          position="top"
                          content={(props: any) => {
                            const row = productChartData[props.index];
                            if (!row || props.x == null) return null;
                            return (
                              <text
                                x={Number(props.x) + Number(props.width ?? 0) / 2}
                                y={Number(props.y) - 8}
                                textAnchor="middle"
                                style={{
                                  fontSize: 11,
                                  fill: "var(--color-admin-text-secondary)",
                                }}
                              >
                                {nok(row.omsetning)} kr
                              </text>
                            );
                          }}
                        />
                      </Bar>
                      <Bar
                        dataKey="sampling"
                        name="Prøvetaking"
                        stackId="kostnad"
                        fill={COLOR.sampling}
                      />
                      <Bar
                        dataKey="markers"
                        name="Biomarkører"
                        stackId="kostnad"
                        fill={COLOR.markers}
                      />
                      <Bar
                        dataKey="assessment"
                        name="Legevurdering"
                        stackId="kostnad"
                        fill={COLOR.assessment}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="mt-2 text-xs text-admin-text-subtle">
                    Kroner per leverte helsesjekk. Den grønne søylen er omsetningen én
                    helsesjekk gir oss; den stablede er hva den koster oss.
                    Avstanden mellom dem er marginen.
                  </p>
                </div>

                <DashboardTable
                  table={productTable}
                  columns={PRODUCT_COLUMNS}
                  headerRows={PRODUCT_HEADER_ROWS}
                  renderRow={(r) => {
                    const t = productRowTotals(r);
                    const {
                      v,
                      per,
                      incomeTotal,
                      costTotal,
                      outstandingIncome,
                      projectedIncome,
                      totals,
                      result,
                      marginPct,
                      avg,
                    } = t;
                    return (
                      <TableRow key={r.flow_type}>
                        <TableCell className="align-top font-medium text-admin-text-primary">
                          {FLOW_LABELS[r.flow_type] ?? r.flow_type}
                        </TableCell>
                        <TableCell className="align-top text-right tabular-nums">
                          {r.companies}
                        </TableCell>
                        <TableCell className="align-top text-right tabular-nums">
                          {count(r.priced_visits)}
                          {r.unpriced_visits > 0 && (
                            <Hint
                              className="ml-1 text-xs text-admin-warn-400"
                              text="Levert, men bedriften mangler inntekt per ansatt, så beløpene under dekker dem ikke"
                            >
                              +{r.unpriced_visits}
                            </Hint>
                          )}
                        </TableCell>
                        <Money
                          divide
                          value={incomeTotal}
                          avg={avg(incomeTotal)}
                          color={COLOR.income}
                          formula={times(
                            `${nok(avg(incomeTotal))} kr i snitt per helsesjekk`,
                            "leverte helsesjekker",
                            v,
                            `${nok(incomeTotal)} kr`,
                          )}
                          avgFormula={`Omsetning ${nok(incomeTotal)} kr ÷ ${count(v)} leverte helsesjekker`}
                        />
                        <Money
                          value={costTotal}
                          avg={avg(costTotal)}
                          formula={`Prøvetaking ${nok(r.sampling_cost)} + biomarkører ${nok(r.marker_cost)} + legevurdering ${nok(r.assessment_cost)} = ${nok(costTotal)} kr`}
                          avgFormula={`Kostnad ${nok(costTotal)} kr ÷ ${count(v)} leverte helsesjekker`}
                        />
                        <Money
                          value={result}
                          avg={avg(result)}
                          color={
                            result != null && result < 0
                              ? COLOR.loss
                              : COLOR.income
                          }
                          formula={`Omsetning ${nok(incomeTotal)} − kostnad ${nok(costTotal)} = ${nok(result)} kr`}
                          avgFormula={`Resultat ${nok(result)} kr ÷ ${count(v)} leverte helsesjekker`}
                        />
                        <MarginCell
                          value={marginPct}
                          formula={`Resultat ${nok(result)} ÷ omsetning ${nok(incomeTotal)} = ${pct(marginPct)}`}
                        />
                        {/* The two projections cover different visits: the
                            backlog's income needs only a rate, its margin
                            needs a measured cost. Each says what it left
                            out rather than sharing one footnote. */}
                        <TotalCell
                          divide
                          value={projectedIncome}
                          add={outstandingIncome}
                          partial={
                            r.outstanding_priced_visits < r.outstanding_visits
                          }
                          partialReason="bedriften mangler inntekt per ansatt, så det finnes ingen sats å prise dem til"
                          outstanding={r.outstanding_visits}
                          covered={r.outstanding_priced_visits}
                          formula={`Så langt ${nok(incomeTotal)} + utestående ${nok(outstandingIncome)} (${count(r.outstanding_priced_visits)} bestilte helsesjekker til bedriftens egen sats) = ${nok(projectedIncome)} kr`}
                        />
                        <TotalCell
                          value={result == null ? null : totals.projected}
                          add={Number(r.outstanding_margin_current)}
                          partial={totals.partial}
                          // Two arms here, not one: this column drops both
                          // the companies without a rate and those where
                          // nothing has ever been delivered at any grain,
                          // and a product row can hold some of each.
                          partialReason={marginGap(r)}
                          outstanding={r.outstanding_visits}
                          covered={r.outstanding_margin_visits}
                          formula={
                            `Så langt ${nok(result)} + utestående margin ${nok(r.outstanding_margin_current)} (${count(r.outstanding_margin_visits)} bestilte helsesjekker × omsetning − kostnad per helsesjekk) = ${nok(totals.projected)} kr` +
                            (r.outstanding_margin_product_visits > 0
                              ? `. ${plural(r.outstanding_margin_product_visits, "av dem er", "av dem er")} priset til produktets snittkostnad, fordi bedriften ikke har levert noen helsesjekk selv`
                              : "")
                          }
                        />
                        <Money
                          divide
                          value={per(r.sampling_cost)}
                          formula={`${nok(r.sampling_cost)} kr i prøvetaking ÷ ${count(v)} leverte helsesjekker`}
                        />
                        <Money
                          value={per(r.marker_cost)}
                          formula={`${nok(r.marker_cost)} kr i biomarkører vi betalte selv ÷ ${count(v)} leverte helsesjekker`}
                        />
                        <Money
                          value={per(r.assessment_cost)}
                          formula={`Legevurdering ${nok(r.assessment_cost)} kr ÷ ${count(v)} leverte helsesjekker. Én sats for alle kunder, så dette er satsen selv`}
                        />
                      </TableRow>
                    );
                  }}
                />
              </>
            )}
          </ChartState>
        </CardContent>
      </Card>

      {totals && totals.outstandingVisits > 0 && (
        <Card>
          {/* The one card on the page the date filter does not move, which is
              exactly why it gets read as disagreeing with the tables. Put that
              in the header where it is seen before the numbers, not in a
              paragraph under them. */}
          <CardHeader className="flex-row flex-wrap items-center gap-3 space-y-0">
            <CardTitle>Utestående — bestilt, ikke tatt ennå</CardTitle>
            <Badge variant="info">Teller per nå — ikke i perioden</Badge>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-admin-text-subtle">
              Resten av siden gjelder perioden du har valgt. Dette kortet
              gjelder <span className="text-admin-text-secondary">i dag</span>:
              en bestilt blodprøve har ingen dato før den tas, så den kan ikke
              plasseres på tidslinjen. Antallet og omsetningen er køen slik den
              står nå, til bedriftens egen sats.
            </p>
            {/* The table header now spells out "så langt + utestående", so this
                only has to say which card is that second term. */}
            <p className="text-xs text-admin-text-subtle">
              Dette er «utestående»-leddet i tabellenes Forventet-kolonner:
              «herav … utestående» under Forventet omsetning er kortet
              «Utestående omsetning», og det samme under Forventet resultat er
              «Utestående margin — dagens Helfo-andel».
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <KpiCard
                label="Helsesjekker"
                value={count(totals.outstandingVisits)}
                hint="bestilt, ikke gjennomført"
                explain="Rader i lab_visits på disse bedriftene som ikke har nådd resultat og ikke er avvist. Bare bestillinger som faktisk finnes — ansatte som ennå ikke er registrert, er ikke med."
              />
              <KpiCard
                label="Utestående omsetning"
                value={`${nok(totals.outstandingIncome)} kr`}
                hint={
                  totals.outstandingPricedVisits < totals.outstandingVisits
                    ? `dekker ${count(totals.outstandingPricedVisits)} av ${count(totals.outstandingVisits)}`
                    : "hele utestående"
                }
                color={COLOR.income}
                explain="Antall utestående helsesjekker ganget med bedriftens omsetning per helsesjekk. Prisen er avtalt, så dette er kjent for enhver bedrift som har en sats lagt inn — uansett produkt."
                formula={times(
                  `${nok(totals.outstandingIncome / Math.max(totals.outstandingPricedVisits, 1))} kr i snitt per helsesjekk`,
                  "utestående helsesjekker med en sats",
                  totals.outstandingPricedVisits,
                  `${nok(totals.outstandingIncome)} kr`,
                )}
              />
              {/* Two margins, not one. HELFO is decided per patient, so the
                  cost of an outstanding check has a floor we can guarantee and
                  a likely case we can only assume. Showing one number would be
                  picking an answer the data does not have. */}
              <KpiCard
                label="Utestående margin — uten Helfo"
                value={
                  totals.outstandingMarginVisits > 0
                    ? `${nok(totals.outstandingMarginWorst)} kr`
                    : "—"
                }
                hint={
                  totals.outstandingMarginVisits > 0
                    ? "verste tilfelle: vi betaler alt selv"
                    : noMarginHint(totals.outstandingPricedVisits)
                }
                explain="Gulvet. Kostnaden er snittet av det bedriftens leverte helsesjekker faktisk har kostet — alle sammen, ikke bare periodens — også der panelet settes per pasient, for da er snittet av panelene vi har tatt det nærmeste en pris som finnes. Derfra antar den at ingen av de utestående blodprøvene får Helfo-dekning: vi betaler markørene staten ellers ville tatt, til vår egen avtalepris, og mister prøvetakingsfritaket som bare gjaldt fordi besøket hadde en Helfo-rekvisisjon. Marginen kan ikke bli lavere enn dette. Dette er et alternativt kostnadsanslag på de samme helsesjekkene, ikke et eget ledd i «Forventet» — derfor er det ingen «herav»-linje i tabellene som svarer til det."
                formula={
                  totals.outstandingMarginVisits > 0
                    ? times(
                        `${nok(totals.outstandingMarginWorst / totals.outstandingMarginVisits)} kr i snitt i margin uten Helfo`,
                        "utestående helsesjekker",
                        totals.outstandingMarginVisits,
                        `${nok(totals.outstandingMarginWorst)} kr`,
                      )
                    : undefined
                }
              />
              <KpiCard
                label="Utestående margin — dagens Helfo-andel"
                value={
                  totals.outstandingMarginVisits > 0
                    ? `${nok(totals.outstandingMarginCurrent)} kr`
                    : "—"
                }
                hint={
                  totals.outstandingMarginVisits > 0
                    ? totals.helfoShare != null
                      ? `antar samme ${pct(totals.helfoShare, 0)} Helfo-dekning som nå`
                      : "antar samme Helfo-dekning som nå"
                    : noMarginHint(totals.outstandingPricedVisits)
                }
                color={
                  totals.outstandingMarginVisits > 0 ? COLOR.income : undefined
                }
                explain="Det sannsynlige tilfellet. Kostnaden er snittet av det bedriftens leverte helsesjekker faktisk har kostet — alle sammen, ikke bare periodens — og viderefører dermed både panelene den faktisk tok og Helfo-andelen den faktisk hadde. Det er en antakelse, ikke en garanti: Helfo avgjøres per pasient, og ingenting her vet hvem som kvalifiserer neste gang."
                formula={
                  totals.outstandingMarginVisits > 0
                    ? times(
                        `${nok(totals.outstandingMarginCurrent / totals.outstandingMarginVisits)} kr i snitt i margin`,
                        "utestående helsesjekker",
                        totals.outstandingMarginVisits,
                        `${nok(totals.outstandingMarginCurrent)} kr`,
                      )
                    : undefined
                }
              />
            </div>
            {/* The one thing on the card the badge above does not cover: the
                cost the margins subtract is read off the period, so these two
                do move with the date filter. It belongs next to them. */}
            {totals.outstandingMarginVisits > 0 && (
              <p className="text-xs text-admin-text-subtle">
                Begge margintallene dekker{" "}
                {count(totals.outstandingMarginVisits)} av{" "}
                {count(totals.outstandingVisits)} utestående helsesjekker.
                Kostnaden de trekker fra er snittet av{" "}
                <span className="text-admin-text-secondary">
                  alle helsesjekker bedriften noen gang har fått levert
                </span>
                , ikke bare periodens — køen telles per nå, så prisen på den
                kan ikke følge datofilteret. Hele dette kortet står derfor
                stille når du endrer datoene. Det virkelige tallet ligger
                mellom de to: hvor nær det havner det høye, avhenger av hvor
                mange som faktisk får Helfo-dekning.
              </p>
            )}
            {/* Three things can be true of an outstanding check, and they are
                not equally bad: no rate at all, a rate but no cost anywhere,
                or a cost borrowed from the product. Each is named rather than
                counted — at these sizes the name is the actionable half. */}
            {outstandingGaps && outstandingGaps.noRate.length > 0 && (
              <p className="text-xs text-admin-warn-400">
                {plural(
                  gapVisits(outstandingGaps.noRate),
                  "utestående helsesjekk er",
                  "utestående helsesjekker er",
                )}{" "}
                hos {namedCompanies(outstandingGaps.noRate)}, som mangler
                inntekt per ansatt. Verken omsetningen eller marginen er kjent,
                og de er ikke med i noen av tallene over — legg inn satsen på
                bedriften for å få dem med.
              </p>
            )}
            {outstandingGaps && outstandingGaps.noCost.length > 0 && (
              <p className="text-xs text-admin-warn-400">
                {plural(
                  gapVisits(outstandingGaps.noCost),
                  "utestående helsesjekk er",
                  "utestående helsesjekker er",
                )}{" "}
                hos {namedCompanies(outstandingGaps.noCost)}, der ingen
                helsesjekk noensinne er levert — heller ikke hos noen annen
                bedrift på samme produkt. Omsetningen er kjent, marginen ikke,
                og et tall her ville vært gjettet.
              </p>
            )}
            {outstandingGaps && outstandingGaps.borrowed.length > 0 && (
              <p className="text-xs text-admin-text-subtle">
                {plural(
                  gapVisits(outstandingGaps.borrowed),
                  "utestående helsesjekk er",
                  "utestående helsesjekker er",
                )}{" "}
                hos {namedCompanies(outstandingGaps.borrowed)}, som ikke har
                levert noen helsesjekk selv. De er priset til{" "}
                <span className="text-admin-text-secondary">
                  snittkostnaden på produktet
                </span>{" "}
                i stedet, som er et grovere anslag: det blander panelene og
                Helfo-flaksen til de andre kundene på samme produkt.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Per bedrift{scopeLabel ? ` — ${scopeLabel}` : ""}</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCompanies}
            disabled={!companyRows.data || companyRows.data.length === 0}
          >
            <Download className="h-3 w-3" />
            Eksporter CSV
          </Button>
        </CardHeader>
        <CardContent className="space-y-5">
          <ChartState
            query={companyRows}
            empty="Ingen bedrifter med leverte helsesjekker i perioden."
          >
            {() => (
              <>
                {priceGroups && priceGroups.groups.length > 1 && (
                  <div className="rounded-md border border-admin-card-border bg-admin-card p-4 space-y-3">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm text-admin-text-secondary">
                        Hva én helsesjekk selges for
                      </p>
                      <p className="text-xs text-admin-text-subtle">
                        Snitt {nok(priceGroups.average)} kr · median{" "}
                        {nok(priceGroups.median)} kr
                      </p>
                    </div>
                    <div className="flex h-7 w-full overflow-hidden rounded">
                      {priceGroups.groups.map((g, i) => (
                        <Hint
                          key={g.price}
                          decorate={false}
                          text={`${nok(g.price)} kr per helsesjekk — ${nok(g.income)} kr, ${g.share.toFixed(1)} % av omsetningen`}
                          className="flex items-center justify-center text-[10px] font-medium text-white"
                          style={{
                            width: `${g.share}%`,
                            backgroundColor: priceShade(i),
                            // The ramp runs light to dark, so neither a light
                            // nor a dark label is readable on every segment.
                            textShadow: "0 1px 2px rgba(0,0,0,0.65)",
                          }}
                        >
                          {g.share >= 14
                            ? `${nok(g.income)} kr · ${g.share.toFixed(0)} %`
                            : g.share >= 7
                              ? `${g.share.toFixed(0)} %`
                              : ""}
                        </Hint>
                      ))}
                    </div>
                    <DashboardTable
                      table={priceGroupTable}
                      columns={PRICE_GROUP_COLUMNS}
                      renderRow={(g) => (
                        <TableRow key={g.price}>
                          <TableCell className="text-admin-text-primary">
                            <span
                              className="mr-2 inline-block h-2 w-2 rounded-full align-middle"
                              style={{
                                backgroundColor: priceShade(
                                  priceGroups.priceIndex.get(g.price) ?? 0,
                                ),
                              }}
                            />
                            {nok(g.price)} kr
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            <Hint text={g.companies.join(", ")}>
                              {g.companies.length}
                            </Hint>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {count(g.visits)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            <Explain
                              formula={times(
                                `${nok(g.price)} kr per helsesjekk`,
                                "leverte helsesjekker",
                                g.visits,
                                `${nok(g.income)} kr`,
                              )}
                            >
                              {nok(g.income)} kr
                            </Explain>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            <Explain
                              formula={`${nok(g.income)} kr ÷ ${nok(priceGroups.totalIncome)} kr i realisert omsetning = ${pct(g.share)}`}
                            >
                              {pct(g.share)}
                            </Explain>
                          </TableCell>
                        </TableRow>
                      )}
                    />
                  </div>
                )}

                <DashboardTable
                  table={companyTable}
                  columns={COMPANY_COLUMNS}
                  headerRows={COMPANY_HEADER_ROWS}
                  renderRow={(p) => (
                    <TableRow key={p.row.company_id}>
                      <TableCell className="align-top font-medium text-admin-text-primary whitespace-nowrap">
                        {p.row.company_name}
                      </TableCell>
                      <TableCell className="align-top text-admin-text-secondary whitespace-nowrap">
                        {FLOW_LABELS[p.row.flow_type] ?? p.row.flow_type}
                      </TableCell>
                      <TableCell className="align-top text-right tabular-nums">
                        {count(p.visits)}
                      </TableCell>
                      {p.incomeTotal == null ? (
                        <TableCell
                          className={cn(
                            "align-top text-right text-xs text-admin-warn-400",
                            GROUP_RULE
                          )}
                        >
                          mangler
                        </TableCell>
                      ) : (
                        <Money
                          divide
                          value={p.incomeTotal}
                          avg={p.income}
                          color={COLOR.income}
                          formula={times(
                            `${nok(p.income)} kr per helsesjekk (${nok(p.row.income_per_employee)} kr per ansatt ÷ ${count(p.row.visits_per_year)} blodprøver i året)`,
                            "leverte helsesjekker",
                            p.visits,
                            `${nok(p.incomeTotal)} kr`,
                          )}
                          avgFormula={`${nok(p.row.income_per_employee)} kr per ansatt i året ÷ ${count(p.row.visits_per_year)} blodprøver = ${nok(p.income)} kr per helsesjekk`}
                        />
                      )}
                      <Money
                        value={p.costTotal}
                        avg={p.cost}
                        formula={`Prøvetaking ${nok(p.row.sampling_cost)} + biomarkører ${nok(p.row.marker_cost)} + legevurdering ${nok(p.row.assessment_cost)} = ${nok(p.costTotal)} kr`}
                        avgFormula={`Kostnad ${nok(p.costTotal)} kr ÷ ${count(p.visits)} leverte helsesjekker`}
                      />
                      <Money
                        value={p.totals.soFar}
                        avg={p.margin}
                        color={
                          p.totals.soFar != null && p.totals.soFar < 0
                            ? COLOR.loss
                            : COLOR.income
                        }
                        // Nothing delivered means no cost to subtract, and
                        // "omsetning 0 − kostnad —" is not arithmetic.
                        formula={
                          p.costTotal == null
                            ? undefined
                            : `Omsetning ${nok(p.incomeTotal)} − kostnad ${nok(p.costTotal)} = ${nok(p.totals.soFar)} kr`
                        }
                        avgFormula={
                          p.cost == null
                            ? undefined
                            : `Omsetning ${nok(p.income)} − kostnad ${nok(p.cost)} per helsesjekk`
                        }
                      />
                      <MarginCell
                        value={p.marginPct}
                        formula={`Resultat ${nok(p.totals.soFar)} ÷ omsetning ${nok(p.incomeTotal)} = ${pct(p.marginPct)}`}
                      />
                      <TotalCell
                        divide
                        value={p.projectedIncome}
                        add={p.outstandingIncome}
                        formula={`Så langt ${nok(p.incomeTotal)} + utestående ${nok(p.outstandingIncome)} (${count(p.row.outstanding_visits)} bestilte × ${nok(p.income)} kr) = ${nok(p.projectedIncome)} kr`}
                      />
                      <TotalCell
                        value={p.totals.projected}
                        add={
                          p.row.outstanding_margin_current == null
                            ? null
                            : Number(p.row.outstanding_margin_current)
                        }
                        unknown={
                          p.totals.projected == null && p.totals.soFar != null
                        }
                        unknownReason={`${p.row.company_name} har aldri fått levert en helsesjekk, og det har ingen annen bedrift på ${FLOW_LABELS[p.row.flow_type] ?? p.row.flow_type} heller. Da finnes det ikke noe snitt å lese kostnaden av, og et tall her ville vært gjettet.`}
                        formula={costBasisFormula(p)}
                        estimate={
                          p.row.cost_basis === "product"
                            ? "produktsnitt"
                            : undefined
                        }
                        estimateReason={`${p.row.company_name} har ikke fått levert en eneste helsesjekk, så det finnes ikke noe eget snitt å prise køen med. Kostnaden er snittet over ${plural(p.row.product_visits, "helsesjekk", "helsesjekker")} levert av ${plural(p.row.product_companies, "annen bedrift", "andre bedrifter")} på samme produkt — et grovere anslag, som blander deres paneler og Helfo-flaks.`}
                      />
                      {p.row.ambiguous_draws > 0 ? (
                        <TableCell
                          className={cn(
                            "align-top text-right tabular-nums text-admin-warn-400 text-xs",
                            GROUP_RULE
                          )}
                        >
                          <Hint
                            text={`${plural(p.row.ambiguous_draws, "blodprøve", "blodprøver")} på en lab med flere priser. Vi vet ikke hvilken som er vår, så de teller som 0 kr.`}
                          >
                            {p.sampling == null ? "—" : nok(p.sampling)} ·
                            mangler pris
                          </Hint>
                        </TableCell>
                      ) : (
                        <Money
                          divide
                          value={p.sampling}
                          formula={`${nok(p.row.sampling_cost)} kr i prøvetaking ÷ ${count(p.visits)} leverte helsesjekker`}
                        />
                      )}
                      <Money
                        value={p.markers}
                        formula={`${nok(p.row.marker_cost)} kr i markører vi betalte selv ÷ ${count(p.visits)} leverte helsesjekker. Helfo dekket i tillegg ${nok(p.row.helfo_savings)} kr`}
                      />
                      <Money
                        value={p.assessment}
                        formula={`${count(p.visits)} leverte helsesjekker × ${nok(p.row.assessment_rate)} kr — én sats for alle kunder, satt i Katalog → Priser`}
                      />
                      <TableCell
                        className={cn(
                          "align-top text-right tabular-nums text-admin-text-secondary",
                          GROUP_RULE
                        )}
                      >
                        {p.row.outstanding_visits ? (
                          <>
                            {count(p.row.outstanding_visits)}
                            {p.row.outstanding_income != null && (
                              <span className="ml-1 text-xs text-admin-text-subtle">
                                ·{" "}
                                <Explain
                                  formula={times(
                                    `${nok(p.income)} kr per helsesjekk`,
                                    "bestilte helsesjekker",
                                    p.row.outstanding_visits,
                                    `${nok(p.row.outstanding_income)} kr`,
                                  )}
                                >
                                  {nok(p.row.outstanding_income)} kr
                                </Explain>
                              </span>
                            )}
                          </>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                />
              </>
            )}
          </ChartState>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Kost prøvetakning{scopeLabel ? ` — ${scopeLabel}` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-admin-text-subtle">
            Hva én blodprøve koster å ta, og hvor tallet kommer fra. Én rad
            per lab som tok blod i perioden, med prisen fra{" "}
            <code>lab_prices</code>. En lab har normalt én pris. Har den
            flere, er de{" "}
            <span className="text-admin-text-secondary">
              alternativer for ulike kunder
            </span>{" "}
            — de legges aldri sammen, og siden ingenting sier hvilken som er
            vår settes ingen pris i det hele tatt.
          </p>
          <ChartState
            query={samplingRates}
            empty="Ingen leverte helsesjekker i perioden."
          >
            {() =>
              sampling && (
                <>
                  <DashboardTable
                    table={samplingTable}
                    columns={SAMPLING_COLUMNS}
                    renderRow={(r) => {
                      const charged = r.visits - r.waived_visits;
                      return (
                        <TableRow key={r.lab_id}>
                          <TableCell className="font-medium text-admin-text-primary whitespace-nowrap">
                            {r.lab_name ?? (
                              <Hint
                                className="text-admin-text-subtle"
                                text="Blodprøven har ingen lab registrert, så vi vet ikke hvor den ble tatt. Den koster 0 kr i prøvetaking."
                              >
                                Uten lab
                              </Hint>
                            )}
                            {r.furst_affiliated && (
                              <Hint
                                className="ml-2 text-xs font-normal text-admin-text-subtle"
                                text="Furst-tilknyttet: prøver som går på Helfo koster ingenting her."
                              >
                                Furst
                              </Hint>
                            )}
                          </TableCell>
                          <TableCell
                            className="text-right tabular-nums"
                            style={
                              r.price == null
                                ? undefined
                                : { color: COLOR.sampling }
                            }
                          >
                            {r.price == null ? (
                              r.price_rows > 1 ? (
                                <Hint
                                  className="text-xs text-admin-warn-400"
                                  text="Flere priser på denne laben. De er alternativer for ulike kunder, og ingenting sier hvilken som er vår — så prøvetakingen teller som 0 kr."
                                >
                                  {count(r.price_rows)} priser
                                </Hint>
                              ) : (
                                <Hint
                                  className="text-xs text-admin-text-subtle"
                                  text="Ingen pris lagt inn på denne laben — den fakturerer oss ikke for prøvetaking."
                                >
                                  ingen pris
                                </Hint>
                              )
                            ) : (
                              nok(r.price)
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-admin-text-subtle whitespace-nowrap">
                            {r.priced_on ??
                              (r.price_rows > 1 ? "ingen brukt" : "—")}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-admin-text-subtle">
                            {count(r.companies)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-admin-text-subtle">
                            <Explain
                              formula={
                                r.waived_visits > 0
                                  ? `${count(r.visits)} blodprøver i perioden − ${count(r.waived_visits)} som gikk gratis på Helfo = ${count(charged)}`
                                  : undefined
                              }
                            >
                              {count(charged)}
                            </Explain>
                            {r.waived_visits > 0 && (
                              <Hint
                                className="ml-1 text-xs"
                                style={{ color: COLOR.helfo }}
                                text="Prøver som gikk på Helfo hos en Furst-tilknyttet lab. Laben tar ingenting for dem."
                              >
                                · {count(r.waived_visits)} gratis
                              </Hint>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            <Explain
                              formula={times(
                                `${nok(r.price ?? 0)} kr per blodprøve`,
                                "betalte prøver",
                                charged,
                                `${nok(r.cost_total)} kr`,
                              )}
                            >
                              {nok(r.cost_total)}
                            </Explain>
                          </TableCell>
                        </TableRow>
                      );
                    }}
                  />
                  {sampling.ambiguous > 0 && (
                    <p className="mt-2 text-xs text-admin-warn-400">
                      {plural(
                        sampling.ambiguous,
                        "blodprøve teller",
                        "blodprøver teller",
                      )}{" "}
                      som 0 kr fordi laben har flere priser og ingenting sier
                      hvilken som er vår. Kostnaden er for lav og marginen for
                      høy for de bedriftene, på hele siden.
                    </p>
                  )}
                  <p className="mt-2 text-xs text-admin-text-subtle">
                    Sum{" "}
                    <span className="text-admin-text-secondary">
                      {nok(sampling.total)} kr
                    </span>
                    {samplingCheck &&
                      (Math.abs(samplingCheck.diff) < 0.5 ? (
                        <> — samme prøvetakning som «Per bedrift» viser. </>
                      ) : (
                        <span className="text-admin-warn-400">
                          {" "}
                          — men «Per bedrift» viser{" "}
                          {nok(samplingCheck.onPage)} kr. Avviket på{" "}
                          {nok(Math.abs(samplingCheck.diff))} kr betyr at noe
                          her ikke stemmer.{" "}
                        </span>
                      ))}
                    «Per produkt» teller bare bedrifter med kjent inntekt per
                    ansatt, og er derfor lavere enn begge.
                  </p>
                </>
              )
            }
          </ChartState>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Markører — privat vs. Helfo{scopeLabel ? ` — ${scopeLabel}` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ChartState
            query={markers}
            empty="Ingen markører på leverte helsesjekker i perioden."
          >
            {(rows) => (
              <>
                <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
                  {/* One row per marker, tall enough that `interval={0}` can
                      draw every label instead of dropping every other one. */}
                  <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-admin-text-secondary">
                    {[
                      ["Privat (vi betaler)", COLOR.markers],
                      ["Helfo (staten betaler)", COLOR.helfo],
                    ].map(([label, color]) => (
                      <span key={label} className="flex items-center gap-1.5">
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-sm"
                          style={{ backgroundColor: color }}
                        />
                        {label}
                      </span>
                    ))}
                  </div>
                  <ResponsiveContainer
                    width="100%"
                    height={40 + markerChartData.length * 26}
                  >
                    <BarChart
                      layout="vertical"
                      data={markerChartData}
                      margin={{ top: 8, right: 72, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                      <XAxis type="number" tick={axisStyle} allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={axisStyle}
                        interval={0}
                        width={150}
                      />
                      <Tooltip content={<MarkerTooltip />} />
                      <Bar
                        dataKey="privat"
                        name="Privat (vi betaler)"
                        stackId="a"
                        fill={COLOR.markers}
                      />
                      <Bar
                        dataKey="helfo"
                        name="Helfo (staten betaler)"
                        stackId="a"
                        fill={COLOR.helfo}
                      >
                        {/* Absolute count plus the Helfo share, so the bar
                            answers "how many" and "how much of it is free"
                            without having to be hovered. */}
                        <LabelList
                          position="right"
                          content={(props: any) => {
                            const row = markerChartData[props.index];
                            if (!row || props.x == null) return null;
                            const share =
                              row.total > 0 ? (row.helfo / row.total) * 100 : 0;
                            return (
                              <text
                                x={Number(props.x) + Number(props.width ?? 0) + 6}
                                y={Number(props.y) + Number(props.height ?? 0) / 2}
                                dominantBaseline="middle"
                                style={{ fontSize: 10, fill: "var(--color-admin-text-subtle)" }}
                              >
                                {count(row.total)}
                                {row.helfo > 0 ? ` · ${share.toFixed(0)} % Helfo` : ""}
                              </text>
                            );
                          }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  {rows.length > MARKER_CHART_ROWS && (
                    <p className="mt-2 text-xs text-admin-text-subtle">
                      Viser de {MARKER_CHART_ROWS} mest brukte markørene.
                      Tabellen under viser alle {rows.length}.
                    </p>
                  )}
                </div>
                <DashboardTable
                  table={markerTable}
                  columns={MARKER_COLUMNS}
                  renderRow={(r) => {
                    const total = r.private_count + r.helfo_count;
                    return (
                      <TableRow key={r.biometric_name}>
                        <TableCell className="text-admin-text-primary">
                          {r.biometric_name}
                          {r.price_missing && (
                            <span className="ml-2 text-xs text-admin-warn-400">
                              mangler pris
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-admin-text-secondary">
                          {nok(r.unit_price)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {count(r.private_count)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <Explain
                            formula={times(
                              `${nok(Number(r.private_cost) / Math.max(r.private_count, 1))} kr i snitt`,
                              "ganger på privat rekvisisjon",
                              r.private_count,
                              `${nok(r.private_cost)} kr`,
                            )}
                          >
                            {nok(r.private_cost)}
                          </Explain>
                        </TableCell>
                        <TableCell
                          className="text-right tabular-nums"
                          style={r.helfo_count ? { color: COLOR.helfo } : undefined}
                        >
                          {count(r.helfo_count)}
                        </TableCell>
                        <TableCell
                          className="text-right tabular-nums"
                          style={r.helfo_count ? { color: COLOR.helfo } : undefined}
                        >
                          <Explain
                            formula={
                              r.helfo_count > 0
                                ? times(
                                    `${nok(Number(r.helfo_savings) / r.helfo_count)} kr i snitt til vår avtalepris`,
                                    "ganger på Helfo-rekvisisjon",
                                    r.helfo_count,
                                    `${nok(r.helfo_savings)} kr vi slapp`,
                                  )
                                : undefined
                            }
                          >
                            {nok(r.helfo_savings)}
                          </Explain>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-admin-text-secondary">
                          {/* No Helfo at all is a dash, not "0 % · 0 kr" —
                              a column of zeroes reads as data. */}
                          {r.helfo_count > 0 ? (
                            <>
                              <Explain
                                formula={`${count(r.helfo_count)} på Helfo av ${count(total)} ganger markøren ble tatt = ${pct((r.helfo_count / total) * 100, 0)}`}
                              >
                                {pct((r.helfo_count / total) * 100, 0)}
                              </Explain>
                              <span className="ml-1 text-xs text-admin-text-subtle">
                                · {nok(r.helfo_savings)} kr
                              </span>
                            </>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }}
                />
              </>
            )}
          </ChartState>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Ramp for the price-distribution bar. Green, because the bar *is* income
 * split by what it was sold for — blue would collide with Helfo, which means
 * something else on every other chart here. Dark to light, because the groups
 * are sorted biggest share first: the widest segments are then the dark ones,
 * where the white label actually reads.
 */
const PRICE_SHADES = [
  "var(--color-admin-success-600)",
  "var(--color-admin-mint-500)",
  "var(--color-admin-success-500)",
  "var(--color-admin-mint-400)",
  "var(--color-admin-success-400)",
  "var(--color-admin-success-300)",
];

function priceShade(index: number): string {
  return PRICE_SHADES[index % PRICE_SHADES.length]!;
}

/** Loading / error / empty wrapper so every card handles the three states the same way. */
function ChartState<T>({
  query,
  empty,
  children,
}: {
  query: {
    isLoading: boolean;
    isError: boolean;
    error?: { message?: string } | null;
    data?: T[];
  };
  empty: string;
  children: (rows: T[]) => React.ReactNode;
}) {
  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 text-admin-text-subtle text-sm py-8 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
        Laster…
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="rounded-md border border-admin-danger-500/30 bg-admin-danger-500/5 p-4 text-sm text-admin-danger-400">
        {query.error?.message || "Ukjent feil"}
      </div>
    );
  }
  if (!query.data || query.data.length === 0) {
    return (
      <p className="text-sm text-admin-text-subtle py-6 text-center">{empty}</p>
    );
  }
  return <>{children(query.data)}</>;
}

function KpiCard({
  label,
  value,
  hint,
  color,
  explain,
  formula,
}: {
  label: string;
  value: string;
  hint?: string;
  color?: string;
  /** What the number means. On the dotted label, so it can be found. */
  explain?: string;
  /** What it is made of, with this card's own figures. On the number. */
  formula?: string;
}) {
  return (
    <Card>
      <CardContent className="px-4 py-3">
        <p className="text-xs text-admin-text-subtle">
          <Hint text={explain}>{label}</Hint>
        </p>
        <p className="text-lg font-bold" style={{ color }}>
          <Explain formula={formula}>{value}</Explain>
        </p>
        {hint && <p className="text-xs text-admin-text-subtle mt-0.5">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function Money({
  value,
  avg,
  color,
  divide,
  formula,
  avgFormula,
}: {
  value: number | null;
  /**
   * The same amount per health check, in small type under the total. The
   * table reports kroner i alt; this is what one check contributed, so the
   * two units sit together instead of in columns that drift apart.
   */
  avg?: number | null;
  color?: string;
  divide?: boolean;
  /** The arithmetic behind the total — see `Explain`. */
  formula?: string;
  /** …and behind the per-check figure, which is a different sum. */
  avgFormula?: string;
}) {
  return (
    <TableCell
      className={cn("align-top text-right tabular-nums", divide && GROUP_RULE)}
      style={value == null ? undefined : { color }}
    >
      {value == null ? (
        "—"
      ) : (
        <Explain formula={formula}>{nok(value)}</Explain>
      )}
      {avg != null && (
        <span className="block text-xs font-normal text-admin-text-subtle">
          <Explain formula={avgFormula}>{nok(avg)}</Explain>
        </span>
      )}
    </TableCell>
  );
}

/**
 * Why outstanding checks fall out of the margin projection.
 * `outstanding_margin_visits` drops two different kinds of company — one with
 * no contract rate, one where nothing has ever been delivered to read a cost
 * off — and the two need opposite fixes, so naming one of them for the whole
 * gap sends the operator to the wrong data. A row can have both.
 *
 * The second arm is far rarer than it was: the rate is now the average over
 * every check the company has ever had delivered, and a company that has
 * never delivered one borrows its product's. Only a product with nothing
 * delivered at all lands here.
 */
function marginGap(row: {
  outstanding_visits: number;
  outstanding_priced_visits: number;
  outstanding_margin_visits: number;
}): string {
  const unpriced = row.outstanding_visits - row.outstanding_priced_visits;
  const undelivered =
    row.outstanding_priced_visits - row.outstanding_margin_visits;
  const noCost =
    "hos bedrifter der ingen helsesjekk noensinne er levert, heller ikke på produktet";
  const noRate = "hos bedrifter uten inntekt per ansatt";
  if (unpriced === 0) return `de er ${noCost}`;
  if (undelivered === 0) return `de er ${noRate}`;
  return `${count(undelivered)} er ${noCost}, ${count(unpriced)} ${noRate}`;
}

/** The same two holes, when the margin covers nothing at all. */
function noMarginHint(outstandingPricedVisits: number): string {
  return outstandingPricedVisits > 0
    ? "ingen helsesjekk levert på hele produktet å regne snittkostnaden av"
    : "ingen inntekt per ansatt lagt inn";
}

/**
 * A kroner total. Unlike `Money` this one has to say *why* it is blank: an
 * empty projection means the backlog's cost is unknowable, not that it is
 * zero, and a partial one means some outstanding checks were left out.
 */
function TotalCell({
  value,
  add,
  partial,
  partialReason,
  unknown,
  unknownReason,
  outstanding,
  covered,
  divide,
  formula,
  estimate,
  estimateReason,
}: {
  value: number | null;
  /**
   * The outstanding part of `value`, in small type underneath. Forventet is
   * "så langt + utestående", and the Utestående card below the table shows
   * only that second term — printing it here is what lets a reader see the
   * two are the same number rather than two answers to one question.
   */
  add?: number | null;
  partial?: boolean;
  /**
   * Why the `+N?` checks were left out, in that column's own terms. The two
   * projections drop different visits for different reasons — income needs a
   * rate, margin needs a delivered check to read a cost off — so one shared
   * sentence sends half the readers looking for the wrong fix.
   */
  partialReason?: string;
  unknown?: boolean;
  /** Why it is unknown, in this row's own terms. */
  unknownReason?: string;
  outstanding?: number;
  covered?: number;
  divide?: boolean;
  /** The arithmetic behind the total — see `Explain`. */
  formula?: string;
  /**
   * A visible tag when the number rests on a weaker estimate than the column
   * normally uses. Everything else on this page reports a gap rather than
   * filling it; the one place we do fill one, it has to say so on the number
   * itself and not only in a tooltip nobody opens.
   */
  estimate?: string;
  estimateReason?: string;
}) {
  const left =
    outstanding != null && covered != null ? outstanding - covered : null;
  return (
    <TableCell
      className={cn("align-top text-right tabular-nums", divide && GROUP_RULE)}
      style={value == null ? undefined : { color: value < 0 ? COLOR.loss : COLOR.income }}
    >
      {value == null ? (
        unknown ? (
          <Hint
            className="text-xs text-admin-text-subtle"
            text={
              unknownReason ??
              "Det er bestilte helsesjekker igjen, men ingen helsesjekk er noensinne levert — heller ikke hos noen annen bedrift på samme produkt — så hva de vil koste er ukjent, og et tall her ville vært gjettet."
            }
          >
            ukjent
          </Hint>
        ) : (
          "—"
        )
      ) : (
        <>
          <Explain formula={formula}>{nok(value)}</Explain>
          {estimate && (
            <Hint
              decorate={false}
              className="ml-1 rounded bg-admin-card-border/60 px-1 text-[10px] font-normal text-admin-text-subtle"
              text={estimateReason}
            >
              {estimate}
            </Hint>
          )}
          {partial && partialReason && left != null && left > 0 && (
            <Hint
              className="ml-1 text-xs text-admin-text-subtle"
              text={`${plural(left, "bestilt helsesjekk", "bestilte helsesjekker")} er ikke med: ${partialReason}.`}
            >
              +{count(left)}?
            </Hint>
          )}
          {add != null && add !== 0 && (
            <Hint
              className="block text-xs font-normal text-admin-text-subtle"
              text="Utestående-delen av tallet over: bestilte helsesjekker som ikke er tatt ennå. Det er den samme summen som «Utestående»-kortet under tabellen viser."
            >
              herav {nok(add)} utestående
            </Hint>
          )}
        </>
      )}
    </TableCell>
  );
}

/** One colour for every margin. Only a loss is called out. */
function marginColor(value: number): string {
  return value < 0 ? COLOR.loss : COLOR.income;
}

function MarginCell({
  value,
  formula,
}: {
  value: number | null | undefined;
  formula?: string;
}) {
  return (
    <TableCell
      className="align-top text-right tabular-nums"
      style={value == null ? undefined : { color: marginColor(value) }}
    >
      {value == null ? (
        pct(value)
      ) : (
        <Explain formula={formula}>{pct(value)}</Explain>
      )}
    </TableCell>
  );
}

/** Kroner per health check, with each cost bucket's share of that product's income. */
function PerVisitTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const income = payload.find((p: any) => p.dataKey === "omsetning")?.value ?? 0;
  // The margin is drawn as a gap, not a bar, so it is not in `payload` — it
  // rides along on the datum every entry carries.
  const row = payload[0]?.payload;
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-admin-text-primary mb-1">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="text-xs" style={{ color: entry.color }}>
          {entry.name}: {nok(entry.value)} kr
          {entry.dataKey !== "omsetning" && income > 0 && (
            <span className="text-admin-text-subtle">
              {" "}
              ({((entry.value / income) * 100).toFixed(0)} % av omsetning)
            </span>
          )}
        </p>
      ))}
      {row?.margin != null && (
        <p
          className="mt-1 border-t border-admin-card-border pt-1 text-xs"
          style={{
            // No percentage means no income to divide by, so the sign of the
            // kroner is all there is to colour on.
            color:
              row.marginPct != null
                ? marginColor(row.marginPct)
                : row.margin < 0
                  ? COLOR.loss
                  : COLOR.income,
          }}
        >
          Resultat: {nok(row.margin)} kr
          {row.marginPct != null && ` · ${pct(row.marginPct, 0)}`}
        </p>
      )}
    </div>
  );
}

function MarkerTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((a: number, p: any) => a + (p.value ?? 0), 0);
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-admin-text-primary mb-1">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="text-xs" style={{ color: entry.color }}>
          {entry.name}: {count(entry.value)}
          {total > 0 && (
            <span className="text-admin-text-subtle">
              {" "}
              ({((entry.value / total) * 100).toFixed(0)} %)
            </span>
          )}
        </p>
      ))}
      <p className="text-xs text-admin-text-subtle mt-1">
        Totalt {count(total)} ganger
      </p>
    </div>
  );
}
